"use client";

import { useLayoutEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { PhoneCall } from "lucide-react";
import { EASE } from "@/lib/animations";
import { ABOUT_HERITAGE_CONTENT } from "@/data/about-heritage";
import { ScrollFillHeadline } from "./ScrollFillHeadline";
import { AntiqueBlueprintSketch } from "./AntiqueBlueprintSketch";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

export function AboutHeritage() {
  const containerRef = useRef<HTMLElement>(null);
  const imageCardRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    headline,
    body,
    accentLink,
    inquiry,
    cta,
    image,
  } = ABOUT_HERITAGE_CONTENT;

  useLayoutEffect(() => {
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduced || !containerRef.current) return;

    // Set initial states BEFORE paint to avoid FOUC
    gsap.set([imageCardRef.current, contentRef.current], {
      opacity: 0,
      y: 60,
      filter: "blur(6px)",
    });

    const ctx = gsap.context(() => {
      // Dual-column reveal with smooth cinematic feel
      gsap.to([imageCardRef.current, contentRef.current], {
        opacity: 1,
        y: 0,
        filter: "blur(0px)",
        duration: 1.2,
        stagger: 0.18,
        ease: EASE.out,
        scrollTrigger: {
          trigger: imageCardRef.current,
          start: "top 82%",
          once: true,
        },
      });

      // Subtle parallax on the image for premium depth
      gsap.to(imageCardRef.current, {
        y: -30,
        ease: "none",
        scrollTrigger: {
          trigger: imageCardRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.2,
        },
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={containerRef}
      className="relative w-full bg-[#fbfbfa] px-6 py-24 sm:px-10 sm:py-32 lg:px-16 overflow-hidden"
    >
      <AntiqueBlueprintSketch />

      <div className="relative z-10 mx-auto max-w-[1240px]">
        {/* ─── HEADLINE ─── */}
        <div className="mb-16 sm:mb-20 max-w-[1100px]">
          <ScrollFillHeadline segments={headline.segments} />
        </div>

        {/* ─── DUAL COLUMN ─── */}
        <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-20 items-center">
          {/* Left — Image */}
          <div
            ref={imageCardRef}
            className="relative rounded-[2.5rem] overflow-hidden shadow-2xl shadow-stone-900/10 aspect-[4/3] sm:aspect-[1.3/1] bg-stone-100 will-change-transform"
          >
            <Image
              src={image.src}
              alt={image.alt}
              fill
              className="object-cover hover:scale-105 transition-transform duration-700 ease-out"
              sizes="(max-width: 1024px) 100vw, 600px"
              priority
            />
            <div className="absolute inset-0 ring-1 ring-black/10 rounded-[2.5rem] pointer-events-none" />
          </div>

          {/* Right — Content */}
          <div ref={contentRef} className="flex flex-col">
            <p className="text-[15px] sm:text-[16px] leading-relaxed text-stone-500 max-w-[540px]">
              {body.intro}{" "}
              <strong className="font-semibold text-stone-800">
                {body.brandName}
              </strong>
              {body.outro}
            </p>

            <div className="mt-6">
              <Link
                href={accentLink.href}
                className="inline-block text-[14px] font-bold text-stone-900 hover:text-amber-800 transition-colors duration-200 border-b-2 border-yellow-400 pb-1"
              >
                {accentLink.label}
              </Link>
            </div>

            <hr className="my-10 border-stone-200" />

            <div className="flex flex-wrap items-center gap-6 mb-10">
              <div className="flex flex-col gap-1">
                <span className="text-[12px] font-bold uppercase tracking-[2px] text-stone-400">
                  {inquiry.eyebrow}
                </span>
                <span className="text-[14px] text-stone-500">
                  {inquiry.description}
                </span>
              </div>

              
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}